import type { Surface } from '@funkit/connect-core'
import type {
  ApplePayEvent,
  AuthEvent,
  ChallengeEvent,
  ConnectEvent,
  ConnectionCheckEvent,
  GetQuoteParams,
} from '@moonpay/platform-sdk-react-native'
import {
  MoonPayApplePayButton,
  MoonPayAuth,
  MoonPayChallenge,
  MoonPayConnect,
  MoonPayConnectionCheck,
  MoonPayProvider,
  useMoonPay,
} from '@moonpay/platform-sdk-react-native'
import { useCallback, useEffect, useState } from 'react'
import { Modal, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { logger } from '../../logging/logger'
import { ScreenSurfaceCover } from '../ScreenSurfaceCover'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import { installMoonPayTrace, moonPayTraceInjection } from './moonpayTrace'

installMoonPayTrace()

const traceWebViewProps = () => ({
  injectedJavaScriptBeforeContentLoaded: moonPayTraceInjection(),
})

const APPLE_PAY_HEIGHT = 44

interface AuthParams {
  sessionToken: string
  environment: string
}

interface PaymentParams extends AuthParams {
  externalTransactionId: string
  quoteInput: GetQuoteParams
}

interface ChallengeParams {
  url: string
}

interface ActiveConnectionResult {
  customer: { id: string }
}

function objectParams(surface: Surface): Record<string, unknown> | undefined {
  return surface.params !== null && typeof surface.params === 'object'
    ? (surface.params as Record<string, unknown>)
    : undefined
}

function authParams(surface: Surface): AuthParams | undefined {
  if (surface.kind !== 'AUTH_COMPONENT' || surface.provider !== 'MOONPAY') {
    return undefined
  }
  const params = objectParams(surface)
  if (params === undefined || typeof params.sessionToken !== 'string') {
    return undefined
  }
  return {
    sessionToken: params.sessionToken,
    environment:
      typeof params.environment === 'string' ? params.environment : 'unstated',
  }
}

function paymentParams(surface: Surface): PaymentParams | undefined {
  if (surface.kind !== 'PAY_SHEET' || surface.provider !== 'MOONPAY') {
    return undefined
  }
  const params = objectParams(surface)
  if (
    params === undefined ||
    typeof params.sessionToken !== 'string' ||
    typeof params.externalTransactionId !== 'string' ||
    params.quoteInput === null ||
    typeof params.quoteInput !== 'object'
  ) {
    return undefined
  }
  return {
    sessionToken: params.sessionToken,
    externalTransactionId: params.externalTransactionId,
    quoteInput: params.quoteInput as GetQuoteParams,
    environment:
      typeof params.environment === 'string' ? params.environment : 'unstated',
  }
}

function challengeParams(surface: Surface): ChallengeParams | undefined {
  if (surface.kind !== 'URL_REDIRECT' || surface.provider !== 'MOONPAY') {
    return undefined
  }
  const url = objectParams(surface)?.url
  return typeof url === 'string' && url.startsWith('https://')
    ? { url }
    : undefined
}

export const moonpay: SurfaceAdapter = (surface) => {
  const auth = authParams(surface)
  if (auth !== undefined) {
    return {
      // The connection check is a background probe in the amount screen's
      // action slot. Only a buyer who is not connected sees the auth modal.
      control: 'EMBEDDED',
      render: (context) => (
        <MoonPayProvider sessionToken={auth.sessionToken}>
          <MoonPayAuthSurface params={auth} context={context} />
        </MoonPayProvider>
      ),
    }
  }
  const payment = paymentParams(surface)
  if (payment !== undefined) {
    return {
      control: 'EMBEDDED',
      render: (context) => (
        <MoonPayProvider sessionToken={payment.sessionToken}>
          <MoonPayPayment params={payment} context={context} />
        </MoonPayProvider>
      ),
    }
  }
  const challenge = challengeParams(surface)
  if (challenge !== undefined) {
    return {
      control: 'HANDOFF',
      render: (context) => (
        <MoonPayProvider>
          <MoonPayKycChallenge params={challenge} context={context} />
        </MoonPayProvider>
      ),
    }
  }
  return undefined
}

function MoonPayKycChallenge({
  params,
  context,
}: {
  params: ChallengeParams
  context: SurfaceContext
}) {
  const onChallenge = useCallback(
    (event: ChallengeEvent) => {
      if (event.kind === 'ready') {
        context.onReady()
      } else if (event.kind === 'complete') {
        context.report({})
      } else if (event.kind === 'cancelled') {
        context.onClose()
      } else {
        context.onFailed(event.payload.code, event.payload.message)
      }
    },
    [context],
  )

  return (
    <SafeAreaView style={styles.screen}>
      <MoonPayChallenge
        url={params.url}
        onEvent={onChallenge}
        style={styles.screen}
        webViewProps={traceWebViewProps()}
      />
    </SafeAreaView>
  )
}

function connectionFailure(
  context: SurfaceContext,
  status: string,
  reason?: string,
) {
  logger.warn('connect-rn:fiat:moonpay_auth_failed', { status, reason })
  context.onFailed(status, reason)
}

function MoonPayAuthSurface({
  params,
  context,
}: {
  params: AuthParams
  context: SurfaceContext
}) {
  const [mode, setMode] = useState<'check' | 'auth' | 'terms'>('check')
  const { client } = useMoonPay()

  const reportActive = useCallback(
    (connection: ActiveConnectionResult) => {
      const credentials = client.getCredentials()
      if (!credentials.ok) {
        connectionFailure(
          context,
          'credentials_unavailable',
          credentials.error.message,
        )
        return
      }
      logger.info('connect-rn:fiat:moonpay_customer_connected', {
        environment: params.environment,
        customerId: connection.customer.id,
      })
      context.report({
        moonPayCustomerId: connection.customer.id,
        moonPayAccessToken: credentials.value.accessToken,
      })
    },
    [client, context],
  )

  const onConnection = useCallback(
    (event: ConnectionCheckEvent) => {
      if (event.kind === 'error') {
        // The stored connection could not be checked or activated. The way
        // back is a fresh authorization, which is what the auth frame is for;
        // failing here only offered the same check again.
        logger.warn('connect-rn:fiat:moonpay_connection_check_failed', {
          environment: params.environment,
          reason: event.payload.message,
        })
        setMode('auth')
        return
      }
      const connection = event.payload
      logger.debug('connect-rn:fiat:moonpay_auth_status', {
        environment: params.environment,
        status: connection.status,
      })
      if (connection.status === 'connectionRequired') {
        setMode('auth')
      } else if (connection.status === 'active') {
        reportActive(connection)
      } else if (connection.status === 'termsAcceptanceRequired') {
        setMode('terms')
      } else {
        connectionFailure(
          context,
          connection.status,
          connection.status === 'failed' ? connection.reason : undefined,
        )
      }
    },
    [context, params.environment, reportActive],
  )

  const onAuth = useCallback(
    (event: AuthEvent) => {
      if (event.kind === 'ready') {
        context.onReady()
      } else if (event.kind === 'error') {
        const reason =
          event.payload.code === 'validationError'
            ? event.payload.errors
                .map((error) => `${error.code}: ${error.message}`)
                .join('; ')
            : event.payload.message
        connectionFailure(context, event.payload.code, reason)
      } else if (event.payload.status === 'active') {
        logger.info('connect-rn:fiat:moonpay_auth_complete', {
          environment: params.environment,
        })
        reportActive(event.payload)
      } else if (event.payload.status === 'termsAcceptanceRequired') {
        setMode('terms')
      } else {
        // The SDK's types end here, but a frame this build has not seen can
        // still complete with another status. Left unanswered, MoonPay's last
        // frame stays up and the step never moves.
        const unmodelled = event.payload as { status?: string; reason?: string }
        connectionFailure(
          context,
          `auth_${unmodelled.status ?? 'unknown'}`,
          unmodelled.reason,
        )
      }
    },
    [context, params.environment, reportActive],
  )

  const onTerms = useCallback(
    (event: ConnectEvent) => {
      if (event.kind === 'ready') {
        context.onReady()
      } else if (event.kind === 'error') {
        connectionFailure(context, 'terms_acceptance_failed')
      } else if (event.payload.status === 'active') {
        reportActive(event.payload)
      } else if (event.payload.status !== 'termsAcceptanceRequired') {
        connectionFailure(
          context,
          event.payload.status,
          event.payload.status === 'failed' ? event.payload.reason : undefined,
        )
      }
    },
    [context, reportActive],
  )

  const modalSurface = (onReady: () => void) => {
    if (mode === 'auth') {
      return (
        <MoonPayAuth
          onEvent={(event) => {
            if (event.kind === 'ready') {
              onReady()
            }
            onAuth(event)
          }}
          style={styles.screen}
          webViewProps={traceWebViewProps()}
        />
      )
    }
    if (mode === 'terms') {
      return (
        <MoonPayConnect
          onEvent={(event) => {
            if (event.kind === 'ready') {
              onReady()
            }
            onTerms(event)
          }}
          style={styles.screen}
          webViewProps={traceWebViewProps()}
        />
      )
    }
    return null
  }

  return (
    <View style={styles.button}>
      {mode === 'check' ? (
        <MoonPayConnectionCheck
          skipKyc
          onEvent={onConnection}
          webViewProps={traceWebViewProps()}
        />
      ) : null}
      <Modal visible={mode !== 'check'} animationType="slide">
        <SafeAreaView style={styles.screen}>
          <ScreenSurfaceCover key={mode} renderSurface={modalSurface} />
        </SafeAreaView>
      </Modal>
    </View>
  )
}

function MoonPayPayment({
  params,
  context,
}: {
  params: PaymentParams
  context: SurfaceContext
}) {
  const { client } = useMoonPay()
  const [connected, setConnected] = useState(false)
  const [needsTerms, setNeedsTerms] = useState(false)
  const [signature, setSignature] = useState<string>()
  const [challengeUrl, setChallengeUrl] = useState<string>()

  const fail = useCallback(
    (code: string, detail?: string) => {
      logger.warn('connect-rn:fiat:moonpay_payment_failed', {
        environment: params.environment,
        code,
        reason: detail,
      })
      context.onFailed(code, detail)
    },
    [context, params.environment],
  )

  const loadQuote = useCallback(async () => {
    logger.debug('connect-rn:fiat:moonpay_payment_quote_started', {
      environment: params.environment,
    })
    const result = await client.getQuote(params.quoteInput)
    if (!result.ok) {
      logger.warn('connect-rn:fiat:moonpay_payment_quote_failed', {
        environment: params.environment,
        code: result.error.code,
      })
      throw new Error(result.error.message ?? result.error.code)
    }
    logger.debug('connect-rn:fiat:moonpay_payment_quote_completed', {
      environment: params.environment,
      executable: result.value.data.executable,
      paymentDisclosureCount: result.value.data.paymentDisclosures.length,
    })
    if (result.value.data.challenge !== undefined) {
      setChallengeUrl(result.value.data.challenge.url)
      return undefined
    }
    if (!result.value.data.executable) {
      throw new Error('quote_not_executable')
    }
    if (result.value.data.paymentDisclosures.length > 0) {
      throw new Error('payment_disclosures_required')
    }
    setSignature(result.value.data.signature)
    return result.value.data.signature
  }, [client, params.quoteInput])

  useEffect(() => {
    if (!connected) {
      return
    }
    void loadQuote().catch((error: unknown) =>
      fail(error instanceof Error ? error.message : 'quote_failed'),
    )
  }, [connected, fail, loadQuote])

  const onConnection = useCallback(
    (event: ConnectionCheckEvent) => {
      if (event.kind === 'error') {
        logger.warn('connect-rn:fiat:moonpay_payment_connection_failed', {
          environment: params.environment,
        })
        fail('connection_check_failed', event.payload.message)
      } else if (event.payload.status === 'active') {
        logger.debug('connect-rn:fiat:moonpay_payment_connection_completed', {
          environment: params.environment,
          status: event.payload.status,
        })
        setConnected(true)
      } else if (event.payload.status === 'termsAcceptanceRequired') {
        logger.debug('connect-rn:fiat:moonpay_payment_connection_completed', {
          environment: params.environment,
          status: event.payload.status,
        })
        setNeedsTerms(true)
      } else {
        logger.warn('connect-rn:fiat:moonpay_payment_connection_failed', {
          environment: params.environment,
          status: event.payload.status,
        })
        fail(`check_${event.payload.status}`)
      }
    },
    [fail, params.environment],
  )

  const onTerms = useCallback(
    (event: ConnectEvent) => {
      if (event.kind === 'ready') {
        context.onReady()
      } else if (event.kind === 'error') {
        fail('terms_acceptance_failed')
      } else if (event.payload.status === 'active') {
        setNeedsTerms(false)
        setConnected(true)
      } else if (event.payload.status !== 'termsAcceptanceRequired') {
        fail(`terms_${event.payload.status}`)
      }
    },
    [context, fail],
  )

  const resetPayment = useCallback(() => {
    setSignature(undefined)
    setConnected(false)
  }, [])

  const reportTransaction = useCallback(
    (transaction: { id?: string; status: string; failureCode?: string }) => {
      // MoonPay reports a closed Apple Pay sheet as a decline, and an issuer
      // decline is also worth another attempt.
      if (
        transaction.status === 'failed' &&
        transaction.failureCode === 'authorizationDeclined'
      ) {
        logger.info('connect-rn:fiat:moonpay_payment_cancelled', {
          environment: params.environment,
          source: 'authorization_declined',
        })
        resetPayment()
      } else if (
        transaction.status === 'failed' ||
        transaction.id === undefined
      ) {
        fail('transaction_failed')
      } else {
        context.report({ paymentAuthorisationId: transaction.id })
      }
    },
    [context, fail, params.environment, resetPayment],
  )

  const onPayment = useCallback(
    (event: ApplePayEvent) => {
      if (event.kind === 'ready') {
        logger.debug('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
        })
        context.onReady()
      } else if (event.kind === 'challenge') {
        logger.debug('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
        })
        setChallengeUrl(event.payload.url)
      } else if (event.kind === 'quoteExpired') {
        logger.warn('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
        })
        void loadQuote()
          .then((freshSignature) => {
            if (freshSignature !== undefined) {
              event.payload.setQuote(freshSignature)
            }
          })
          .catch((error: unknown) =>
            fail(error instanceof Error ? error.message : 'quote_failed'),
          )
      } else if (event.kind === 'unsupported') {
        logger.warn('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
        })
        fail('apple_pay_unsupported')
      } else if (event.kind === 'cancelled') {
        logger.info('connect-rn:fiat:moonpay_payment_cancelled', {
          environment: params.environment,
          source: 'event',
        })
        resetPayment()
      } else if (event.kind === 'complete') {
        logger.debug('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
          status: event.payload.transaction.status,
        })
        reportTransaction(event.payload.transaction)
      } else if (event.kind === 'error') {
        logger.warn('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
          code: event.payload.kind,
        })
        fail(event.payload.kind, event.payload.message)
      } else {
        logger.debug('connect-rn:fiat:moonpay_apple_pay_event', {
          environment: params.environment,
          kind: event.kind,
        })
      }
    },
    [
      context,
      fail,
      loadQuote,
      params.environment,
      reportTransaction,
      resetPayment,
    ],
  )

  const onChallenge = useCallback(
    (event: ChallengeEvent) => {
      if (event.kind === 'cancelled') {
        setChallengeUrl(undefined)
      } else if (event.kind === 'error') {
        fail(event.payload.code, event.payload.message)
      } else if (event.kind === 'complete') {
        setChallengeUrl(undefined)
        if (event.payload.flow === 'buy') {
          reportTransaction(event.payload.transaction)
        } else if (
          event.payload.flow === 'identity' ||
          event.payload.status === 'upgraded'
        ) {
          void loadQuote().catch((error: unknown) =>
            fail(error instanceof Error ? error.message : 'quote_failed'),
          )
        } else {
          fail(`challenge_${event.payload.status}`)
        }
      }
    },
    [fail, loadQuote, reportTransaction],
  )

  return (
    <View style={styles.button}>
      {needsTerms ? (
        <MoonPayConnect
          onEvent={onTerms}
          style={styles.screen}
          webViewProps={traceWebViewProps()}
        />
      ) : !connected ? (
        <MoonPayConnectionCheck
          skipKyc
          onEvent={onConnection}
          webViewProps={traceWebViewProps()}
        />
      ) : signature === undefined ? null : (
        <MoonPayApplePayButton
          quote={signature}
          externalTransactionId={params.externalTransactionId}
          onEvent={onPayment}
          style={styles.button}
          webViewProps={traceWebViewProps()}
        />
      )}
      <Modal visible={challengeUrl !== undefined} animationType="slide">
        <SafeAreaView style={styles.screen}>
          {challengeUrl === undefined ? null : (
            <MoonPayChallenge
              url={challengeUrl}
              onEvent={onChallenge}
              style={styles.screen}
              webViewProps={traceWebViewProps()}
            />
          )}
        </SafeAreaView>
      </Modal>
    </View>
  )
}

const styles = StyleSheet.create({
  screen: { flex: 1, width: '100%', backgroundColor: '#FFFFFF' },
  button: { height: APPLE_PAY_HEIGHT, width: '100%', overflow: 'hidden' },
})
